import type {
  CatalogProduct,
  FeatureGroup,
  FeatureItem,
} from '@aibees/shared/definition';
import type { CatalogCellData, CatalogFeature, ProductCards } from './types';
import { matchColumnKey } from './utils';

/**
 * Compose a single card line per §4a of the FE migration guide: value + unit
 * (+ scalable), with `Disabled`/absent rendered muted and coming-soon flagged
 * from this column's own cell. All values are pre-computed server-side, so the
 * FE only formats them — no client-side seat/limit/rate math.
 */
function cardLine(
  cell: CatalogCellData | undefined,
  feature: CatalogFeature
): { text: string; muted: boolean; comingSoon: boolean } {
  const value = cell?.valuesPerPlan?.value;
  const comingSoon = !!(cell?.comingSoon || cell?.valuesPerPlan?.comingSoon);
  const muted = !cell || cell.disabled === true || value === 'Disabled';

  let text: string;
  if (cell?.unit && value && value !== 'Disabled') {
    // value + unit → "5 API Requests per Second"; scalable → "+5 … (Scalable)".
    const prefix = cell.scalable ? '+' : '';
    const suffix = cell.scalable ? ' (Scalable)' : '';
    text = `${prefix}${value} ${cell.unit}${suffix}`;
  } else if (value) {
    text = value; // e.g. "Unlimited", "10K", "Disabled"
  } else {
    text = feature.name; // boolean feature → its name (rendered with a ✓)
  }

  return { text, muted, comingSoon };
}

function buildFeatureItem(
  feature: CatalogFeature,
  product: CatalogProduct
): FeatureItem {
  const columnKey = matchColumnKey(product, Object.keys(feature.cells ?? {}));
  const cell = columnKey ? feature.cells?.[columnKey] : undefined;
  const { text, muted, comingSoon } = cardLine(cell, feature);

  const item: FeatureItem = {
    id: cell?.code || feature.key,
    title: text,
    description: cell?.tooltip,
    disabled: muted,
    comingSoon,
  };

  if (cell?.badge) {
    item.poweredBy = cell.badge.replace(/^Powered by /, '');
  }

  return item;
}

/**
 * Build a plan card's inclusion list from the pre-grouped `return=cards`
 * response. Sections and their features arrive server-sorted (by `sectionRank`
 * / `cardOrder`) with `children` always empty and section-less features already
 * excluded, so the FE renders them in array order — no client grouping/sorting.
 */
export function buildFeatureGroups(
  productId: string,
  cards: ProductCards
): FeatureGroup[] {
  const product = cards.products.find((p) => p.id === productId);
  if (!product) return [];

  return cards.sections.map((section) => ({
    title: section.name,
    features: section.features.map((feature) =>
      buildFeatureItem(feature, product)
    ),
  }));
}
